"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const NamespacedNamed_1 = require("../NamespacedNamed");
class DeserializeContext {
    constructor(parent) {
        this.parent = parent;
        this.namespaces = new Map();
    }
    lookupNamespace(prefix) {
        const result = this.namespaces.get(prefix);
        if (result) {
            return result;
        }
        if (this.parent) {
            return this.parent.lookupNamespace(prefix);
        }
        throw new Error(`Prefix "${prefix}" is not defined.`);
    }
}
exports.DeserializeContext = DeserializeContext;
exports.DefaultDeserializeContext = new DeserializeContext(undefined);
class SerializeContext {
    constructor() {
        this.key = 1;
        this.namespaces = new Map();
    }
    getPrefixForNamespace(ns) {
        let prefix = this.namespaces.get(ns.namespace);
        if (!prefix) {
            prefix = `p${this.key++}`;
            this.namespaces.set(ns.namespace, prefix);
        }
        return prefix;
    }
}
exports.SerializeContext = SerializeContext;
class DefaultSerializeContext extends SerializeContext {
    getPrefixForNamespace(ns) {
        throw new Error("Not in object context!");
    }
}
class BaseSerializer {
    serialize(value) {
        return this.serializeWithContext(value, new DefaultSerializeContext());
    }
    deserialize(value) {
        return this.deserializeWithContext(value, exports.DefaultDeserializeContext);
    }
    deserializeTyped(value, context) {
        return this.deserializeWithContext(value, context);
    }
    get TValue() {
        throw new Error();
    }
    get TSource() {
        throw new Error();
    }
    getSerializerFor(type) {
        return undefined;
    }
    defineAs(fqn) {
        return new NamedSerializer(this, fqn, true);
    }
    knownAs(fqn) {
        return new NamedSerializer(this, fqn, false);
    }
    refine(options) {
        return new RefinedSerializer(this, options);
    }
}
exports.BaseSerializer = BaseSerializer;
class RefinedSerializer extends BaseSerializer {
    constructor(underlyingSerializer, options) {
        super();
        this.underlyingSerializer = underlyingSerializer;
        this.options = options;
    }
    canSerialize(value) {
        return this.options.canSerialize(value);
    }
    serializeWithContext(value, context) {
        const intermediate = this.options.serialize(value);
        return this.underlyingSerializer.serializeWithContext(intermediate, context);
    }
    deserializeWithContext(value, context) {
        const r = this.underlyingSerializer.deserializeWithContext(value, context);
        if (!r.isOk) {
            return r;
        }
        else {
            return this.options.deserialize(r.value);
        }
    }
    getType(typeSystem) {
        return this.underlyingSerializer.getType(typeSystem);
    }
}
class DelegatingSerializer extends BaseSerializer {
    canSerialize(value) {
        return this.underlyingSerializer.canSerialize(value);
    }
    serializeWithContext(value, context) {
        return this.underlyingSerializer.serializeWithContext(value, context);
    }
    deserializeWithContext(value, context) {
        return this.underlyingSerializer.deserializeWithContext(value, context);
    }
}
exports.DelegatingSerializer = DelegatingSerializer;
class NamedSerializer extends DelegatingSerializer {
    constructor(underlyingSerializer, namespacedName, isDefinition) {
        super();
        this.underlyingSerializer = underlyingSerializer;
        this.namespacedName = namespacedName;
        this.isDefinition = isDefinition;
    }
    serializeWithContext(value, context) {
        let newContext = false;
        if (context instanceof DefaultSerializeContext) {
            context = new SerializeContext();
            newContext = true;
        }
        const r = this.underlyingSerializer.serializeWithContext(value, context);
        if (newContext) {
            if (typeof r !== "object" || Array.isArray(r) || !r) {
                throw new Error("Invalid context");
            }
            r["$ns"] = Object.fromEntries([...context.namespaces.entries()].map(([key, value]) => [
                value,
                key,
            ]));
        }
        return r;
    }
    getSerializerFor(type) {
        if (type.equals(this.namespacedName)) {
            return this.underlyingSerializer;
        }
        return this.underlyingSerializer.getSerializerFor(type);
    }
    deserializeWithContext(value, context) {
        if (typeof value === "object" && value && "$ns" in value) {
            const ns = value["$ns"];
            delete value["$ns"];
            context = new DeserializeContext(context);
            for (const [prefix, nsValue] of Object.entries(ns)) {
                if (typeof nsValue !== "string") {
                    throw new Error(`${nsValue} has invalid type. Must be string.`);
                }
                context.namespaces.set(prefix, NamespacedNamed_1.namespace(nsValue));
            }
        }
        let type = undefined;
        if (typeof value === "object" && value && "$type" in value) {
            const typeName = value["$type"];
            const regExp = /(.*)#(.*)/;
            const m = regExp.exec(typeName);
            if (m) {
                const nsPrefix = m[1];
                const name = m[2];
                const ns = context.lookupNamespace(nsPrefix);
                type = ns(name);
            }
            else {
                throw new Error(`Malformed type "${typeName}"`);
            }
            delete value["$type"];
        }
        if (type) {
            const s = this.getSerializerFor(type);
            if (!s) {
                throw new Error(`Invalid type ${type}.`);
            }
            return s.deserializeWithContext(value, context);
        }
        return super.deserializeWithContext(value, context);
    }
    getType(typeSystem) {
        if (this.isDefinition && !typeSystem.isTypeKnown(this.namespacedName)) {
            typeSystem.getOrCreateType(this.namespacedName);
            const definingType = this.underlyingSerializer.getType(typeSystem);
            typeSystem.defineType(this.namespacedName, definingType);
        }
        return typeSystem.getOrCreateType(this.namespacedName);
    }
}
exports.NamedSerializer = NamedSerializer;
//# sourceMappingURL=Serializer.js.map